import DOMPurify from "dompurify";
import type { Config } from "tailwindcss";
import { t } from "@/i18n-js/instance";

export const mountPointId = "tailwind-frame-mount";
export const mountPointSelector = `#${mountPointId}`;
const translationRoot =
  "settings.site.pages.builder.settings.custom_code.errors";

export interface InitialContent {
  bodyEnd?: string;
  bodyStart?: string;
  headEnd?: string;
  headStart?: string;
  tailwindConfig?: Partial<Config>;
}

const interFontCSS = `
<style>
  @font-face {
    font-family: InterVariable;
    font-style: normal;
    font-weight: 100 900;
    font-display: swap;
    src: url("https://rsms.me/inter/font-files/InterVariable.woff2?v=4.0") format("woff2");
  }
</style>
`;

const defaultTailwindConfig = {
  theme: {
    fontFamily: {
      sans: ["InterVariable", "ui-sans-serif", "system-ui", "sans-serif"],
    },
  },
};

const sanitizeSnippet = (code: string): string => {
  try {
    if (!code || typeof code !== "string") return "";

    const wrapped = `<div>${code}</div>`;

    const sanitized = DOMPurify.sanitize(wrapped, {
      ALLOWED_TAGS: ["style", "meta", "link", "script"],
      KEEP_CONTENT: true,
    });
    return sanitized.replace(/^<div>/, "").replace(/<\/div>$/, "");
  } catch (error) {
    console.error(t([translationRoot, "error_sanitizing_snippet"]), error);
    return "";
  }
};

const validateUserCode = (
  code: string,
): { isValid: boolean; error?: string } => {
  try {
    if (!code || typeof code !== "string") return { isValid: true };

    if (code.length > 10000) {
      return {
        isValid: false,
        error: t([translationRoot, "code_too_long"]),
      };
    }

    const hasHeadTag = /<\s*head\s*>/i.test(code);
    const hasBodyTag = /<\s*body\s*>/i.test(code);
    const hasHtmlTag = /<\s*html\s*>/i.test(code);
    const hasDoctype = /<!DOCTYPE/i.test(code);

    if (hasHeadTag || hasBodyTag || hasHtmlTag || hasDoctype) {
      return {
        isValid: false,
        error: t([
          translationRoot,
          "user_code_should_not_contain_html_head_body_doctype_tags",
        ]),
      };
    }

    return { isValid: true };
  } catch (error) {
    console.error(t([translationRoot, "error_validating_user_code"]), error);
    return {
      isValid: false,
      error: t([translationRoot, "unable_to_validate_code_please_try_again"]),
    };
  }
};

const validateAndLog = (code: string, label: string) => {
  const result = validateUserCode(code);
  if (!result.isValid) {
    console.error(
      t([translationRoot, "validation_failed"]),
      label,
      result.error,
    );
  }
  return result;
};

export const getInitialContent = ({
  headStart = "",
  headEnd = "",
  bodyStart = "",
  bodyEnd = "",
  tailwindConfig = defaultTailwindConfig,
}: InitialContent) => {
  try {
    validateAndLog(headStart, t([translationRoot, "head_start"]));
    validateAndLog(headEnd, t([translationRoot, "head_end"]));
    validateAndLog(bodyStart, t([translationRoot, "body_start"]));
    validateAndLog(bodyEnd, t([translationRoot, "body_end"]));

    const sanitizedHeadStart = sanitizeSnippet(headStart);
    const sanitizedHeadEnd = sanitizeSnippet(headEnd);
    const sanitizedBodyStart = sanitizeSnippet(bodyStart);
    const sanitizedBodyEnd = sanitizeSnippet(bodyEnd);

    let tailwindConfigString = "{}";
    try {
      tailwindConfigString = JSON.stringify(tailwindConfig);
    } catch (error) {
      console.error(t([translationRoot, "error_with_tailwind_config"]), error);
    }

    return `
  <!DOCTYPE html>
  <html>
    <head>
      ${interFontCSS}${sanitizedHeadStart}

      <style>
        html, body, ${mountPointSelector} {
          width: 100%;
          height: 100%;
          overflow: hidden;
        }

        body {
          min-height: 0px;
        }

        ${mountPointSelector} .frame-content {
          width: 100%;
          position: relative;
        }

        ${mountPointSelector} .frame-content .wb-h-full {
          height: auto;
        }
      </style>

      <!-- The below script comes from https://unpkg.com/tailwindcss-cdn@3.4.10/tailwindcss-with-all-plugins.js -->
      <script src="${window.TAILWIND_ASSET_PATH || "/assets/tailwindcss-with-all-plugins.js"}"></script>

      <script>
        try {
          tailwind.config = ${tailwindConfigString};
        } catch (error) {
          console.error('Error applying tailwind config:', error);
        }
      </script>

      ${sanitizedHeadEnd}
    </head>
    <body>
      ${sanitizedBodyStart}
      <div id="${mountPointId}"></div>
      ${sanitizedBodyEnd}
    </body>
  </html>
`;
  } catch (error) {
    console.error(
      t([translationRoot, "critical_error_in_getInitialContent"]),
      error,
    );

    return `
  <!DOCTYPE html>
  <html>
    <head>
      ${interFontCSS}
      <style>
        html, body, ${mountPointSelector} {
          width: 100%;
          height: 100%;
          overflow: hidden;
        }
        body {
          min-height: 0px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #d32f2f;
          font-family: system-ui, sans-serif;
        }
      </style>
    </head>
    <body>
      <div>Error loading frame content. Please check your code.</div>
      <div id="${mountPointId}"></div>
    </body>
  </html>
`;
  }
};
